import React from 'react';

import PropTypes from 'prop-types';

import { Box, Stack, Typography } from '@mui/material';

import { ShieldCheck, ExternalLink, FileX } from 'lucide-react';

import { COLORS } from '../../../Constant/COLORS';

import GreyBox from './GreyBox';
import { TypoPoppins } from '../../../Components/Typo';

const STATUS_STYLES = {
  ACCEPTED: { label: 'Verified', color: COLORS.green4, bg: 'rgba(10,180,118,0.14)' },
  REQUESTED: { label: 'Pending review', color: '#9a7a00', bg: 'rgba(235,208,67,0.22)' },
  REJECTED: { label: 'Rejected', color: COLORS.red3, bg: 'rgba(255,33,33,0.10)' },
};

function StatusChip({ status }) {
  const style = STATUS_STYLES[String(status).toUpperCase()] || STATUS_STYLES.REQUESTED;
  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: 999,
        bgcolor: style.bg,
        color: style.color,
        fontWeight: 700,
        fontSize: '0.75rem',
        whiteSpace: 'nowrap',
      }}>
      {style.label}
    </Box>
  );
}

StatusChip.propTypes = { status: PropTypes.string };

function DocPreview({ label, src }) {
  return (
    <Box flex={1} minWidth={0}>
      <TypoPoppins variant='caption' color={COLORS.inkSoft} mb={0.75} display='block'>
        {label}
      </TypoPoppins>
      {src ? (
        <Box
          component='a'
          href={src}
          target='_blank'
          rel='noreferrer'
          sx={{
            display: 'block',
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            border: `1px solid ${COLORS.hairline}`,
            '&:hover .doc-overlay': { opacity: 1 },
          }}>
          <Box
            component='img'
            src={src}
            alt={label}
            sx={{ width: '100%', height: 150, objectFit: 'cover', display: 'block' }}
          />
          <Stack
            className='doc-overlay'
            direction='row'
            alignItems='center'
            justifyContent='center'
            gap={0.75}
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(1,42,52,0.55)',
              color: '#fff',
              opacity: 0,
              transition: 'opacity .2s ease',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}>
            <ExternalLink size={16} /> View full
          </Stack>
        </Box>
      ) : (
        <Stack
          alignItems='center'
          justifyContent='center'
          gap={1}
          sx={{
            height: 150,
            borderRadius: 2,
            border: `1px dashed ${COLORS.hairline}`,
            bgcolor: COLORS.surfaceAlt,
            color: COLORS.inkSoft,
          }}>
          <FileX size={22} />
          <TypoPoppins variant='caption'>Not submitted</TypoPoppins>
        </Stack>
      )}
    </Box>
  );
}

DocPreview.propTypes = { label: PropTypes.string, src: PropTypes.string };

const VerificationCard = ({ status, nicImage, addressProof, ...rest }) => (
  <GreyBox p={{ xs: 3, md: 4 }} mt={3} {...rest}>
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      flexWrap='wrap'
      gap={2}
      mb={3}>
      <Stack direction='row' alignItems='center' gap={1.25}>
        <ShieldCheck size={20} color={COLORS.teal} strokeWidth={1.8} />
        <Typography variant='subtitle1' fontWeight={600}>
          Verification documents
        </Typography>
      </Stack>
      {status && <StatusChip status={status} />}
    </Stack>

    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
      <DocPreview label='NIC Image' src={nicImage} />
      <DocPreview label='Address Proof' src={addressProof} />
    </Stack>
  </GreyBox>
);

VerificationCard.propTypes = {
  status: PropTypes.string,
  nicImage: PropTypes.string,
  addressProof: PropTypes.string,
};

export default VerificationCard;
